import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCompanies } from "./hooks";
import { useCurrentDomain } from "./context/DomainContext";
import {
  manageFailedBulkBillingCompanyConfig,
  getFailedBulkBillingCompanyConfigs   
} from "./services/masterDataService";

const BRAND_GREEN = '#0aa375';

const Field = ({ label, required }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <span style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
      {label}
      {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
    </span>
  </div>
);

const Spinner = ({ size = 'sm', color = BRAND_GREEN }) => (
  <div className="text-center py-4">
    <div className={`spinner-border spinner-border-${size}`} style={{ color }} role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const CompanyManagement = () => {
  const { domain } = useCurrentDomain();
  const { companies, loading: companiesLoading } = useCompanies(domain);

  const currentUser = sessionStorage.getItem("username") || "System";

  const [companySearch, setCompanySearch] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [addLoading, setAddLoading] = useState(false);

  const [addedCompanies, setAddedCompanies] = useState([]);
  const [addedLoading, setAddedLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchAddedCompanies = useCallback(async () => {
    if (!domain) return;
    setAddedLoading(true);
    try {
      const data = await getFailedBulkBillingCompanyConfigs(domain);
      setAddedCompanies(data || []);
      setSelectedRows([]);
      setSelectAll(false);
    } catch (err) {
      toast.error('Failed to load configured companies.');
    } finally {
      setAddedLoading(false);
    }
  }, [domain]);

  useEffect(() => {
    fetchAddedCompanies();
  }, [fetchAddedCompanies]);

  const toggleCompany = (code) => {
    setSelectedCompanies(prev =>
      prev.includes(code) ? prev.filter(id => id !== code) : [...prev, code]
    );
  };

  const handleAddMapping = async () => {
    if (selectedCompanies.length === 0) return;
    setAddLoading(true);
    const loadingToast = toast.loading('Adding mapping...');
    try {
      const responseData = await manageFailedBulkBillingCompanyConfig({
        companyIds: selectedCompanies,
        actionBy: currentUser,
        domain: domain,
        action: 'ADD'
      });
      toast.dismiss(loadingToast);
      toast.success(responseData.message || `${selectedCompanies.length} company mapping(s) added successfully.`);
      setSelectedCompanies([]);
      setCompanySearch('');
      await fetchAddedCompanies();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err?.response?.data?.message || 'Failed to add mapping.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteMapping = () => {
    if (selectedRows.length === 0) return;
    const toastId = toast(
      <div style={{ textAlign: 'center', minWidth: '250px' }}>
        <div style={{ fontWeight: '600', marginBottom: '10px' }}>
          Delete {selectedRows.length} mapping(s)?
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => toast.dismiss(toastId)}>
            Cancel
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => { toast.dismiss(toastId); performDelete(); }}>
            Yes, Delete
          </button>
        </div>
      </div>,
      { autoClose: false, closeButton: false, closeOnClick: false, draggable: false, className: 'bg-white shadow' }
    );
  };

  const performDelete = async () => {
    setDeleteLoading(true);
    const loadingToast = toast.loading('Deleting mappings...');
    try {
      const responseData = await manageFailedBulkBillingCompanyConfig({
        companyIds: selectedRows,
        actionBy: currentUser,
        domain: domain,
        action: 'DELETE'
      });
      toast.dismiss(loadingToast);
      toast.success(responseData.message || `${selectedRows.length} mapping(s) deleted successfully.`);
      await fetchAddedCompanies();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err?.response?.data?.message || 'Failed to delete mapping.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const addedCompanyIds = addedCompanies.map(c => c.companyId);

  // Lookup map: companyId -> company label, used as a fallback to display
  // the company name alongside companyId in the "Mapped Companies" table
  // in case the backend's CompanyName join ever returns null for a row.
  const companyNameById = companies.reduce((acc, c) => {
    acc[c.value] = c.label;
    return acc;
  }, {});

  const filteredCompanies = companies
    .filter(c => !addedCompanyIds.includes(c.value))
    .filter(c => (c.label || '').toLowerCase().includes(companySearch.toLowerCase()));

  const allMappedSelected =
    addedCompanies.length > 0 &&
    addedCompanies.every(m => selectedRows.includes(m.companyId));

  const handleSelectAll = () => {
    if (allMappedSelected) {
      setSelectedRows([]);
      setSelectAll(false);
    } else {
      setSelectedRows(addedCompanies.map(m => m.companyId));
      setSelectAll(true);
    }
  };

  const toggleRow = (companyId) => {
    setSelectedRows(prev =>
      prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
    );
  };

  return (
    <div className="container-fluid p-0 px-1">
      <div className="card-body">
        <div className="row g-2">

          {/* Left card */}
          <div className="col-12 col-lg-6">
            <div className="card border h-100" style={{ borderRadius: '6px' }}>
              <div className="card-header" style={{ color: '#fff', fontWeight: 600, fontSize: 14, padding: '8px', backgroundColor: BRAND_GREEN }}>
                Add Mapping
              </div>
              <div className="card-body p-2">

                <div className="mb-2">
                  <Field label="Search Companies" />
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Filter company list..."
                    value={companySearch}
                    onChange={e => setCompanySearch(e.target.value)}
                    style={{ fontSize: '12px' }}
                  />
                </div>

                <div style={{ height: '280px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fafafa' }}>
                  {companiesLoading ? (
                    <Spinner />
                  ) : filteredCompanies.length === 0 ? (
                    <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                      No companies available.
                    </div>
                  ) : (
                    filteredCompanies.map(c => (
                      <label
                        key={c.value}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '12px' }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCompanies.includes(c.value)}
                          onChange={() => toggleCompany(c.value)}
                          style={{ accentColor: BRAND_GREEN }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {c.label}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                <div className="d-flex justify-content-center gap-2 mt-2">
                  <button
                    onClick={() => { setSelectedCompanies([]); setCompanySearch(''); }}
                    className="btn btn-outline-secondary btn-sm px-3"
                    style={{ fontSize: '12px' }}
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleAddMapping}
                    disabled={addLoading || selectedCompanies.length === 0}
                    className="btn btn-sm text-white px-3 fw-semibold"
                    style={{ backgroundColor: BRAND_GREEN, border: 'none', fontSize: '12px', opacity: (addLoading || selectedCompanies.length === 0) ? 0.65 : 1 }}
                  >
                    {addLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Saving...
                      </>
                    ) : (
                      `Add Mapping (${selectedCompanies.length})`
                    )}
                  </button>
                </div>

              </div>
            </div>
          </div>

          {/* Right card */}
          <div className="col-12 col-lg-6">
            <div className="card border h-100" style={{ borderRadius: '6px' }}>
              <div className="card-header" style={{ color: '#fff', fontWeight: 600, fontSize: 14, padding: '8px', backgroundColor: BRAND_GREEN }}>
                Mapped Companies
              </div>
              <div className="card-body p-2">

                {addedCompanies.length > 0 && (
                  <div className="d-flex align-items-center justify-content-between mb-1 px-1 py-1"
                    style={{ borderBottom: '1px solid #e9ecef', minHeight: '28px' }}>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        id="selectAllMapped"
                        checked={allMappedSelected}
                        onChange={handleSelectAll}
                        style={{ accentColor: BRAND_GREEN, width: '14px', height: '14px', cursor: 'pointer' }}
                      />
                      <label className="form-check-label mb-0" style={{ fontSize: '11px', fontWeight: 600, color: '#495057', cursor: 'pointer', userSelect: 'none' }} htmlFor="selectAllMapped">
                        Select All
                      </label>
                      <span style={{ fontSize: '10px', color: '#adb5bd' }}>
                        ({addedCompanies.length} total)
                      </span>
                    </div>
                    {selectedRows.length > 0 && (
                      <span style={{
                        fontSize: '10px', fontWeight: 600,
                        background: '#e6f7f2', color: BRAND_GREEN,
                        padding: '2px 8px', borderRadius: '20px',
                        border: `1px solid ${BRAND_GREEN}22`
                      }}>
                        ✓ {selectedRows.length} selected
                      </span>
                    )}
                  </div>
                )}

                <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                  {addedLoading ? (
                    <Spinner />
                  ) : addedCompanies.length === 0 ? (
                    <div className="alert alert-success small py-1 mb-0 m-2">
                      No active mappings.
                    </div>
                  ) : (
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                        <tr>
                          <th style={{ width: '36px', padding: '6px 10px' }}></th>
                          <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Company Id</th>
                          <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Company Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addedCompanies.map(item => (
                          <tr key={item.companyId} style={{ background: selectedRows.includes(item.companyId) ? '#e6f7f2' : 'white' }}>
                            <td style={{ padding: '6px 10px' }}>
                              <input
                                type="checkbox"
                                checked={selectedRows.includes(item.companyId)}
                                onChange={() => toggleRow(item.companyId)}
                                style={{ accentColor: BRAND_GREEN }}
                              />
                            </td>
                            <td style={{ padding: '6px 10px', fontWeight: 500 }}>
                              {item.companyId}
                            </td>
                            <td style={{ padding: '6px 10px', color: '#334155', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.companyName || companyNameById[item.companyId] || ''}>
                              {item.companyName || companyNameById[item.companyId] || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="d-flex justify-content-center mt-2">
                  <button
                    onClick={handleDeleteMapping}
                    disabled={deleteLoading || selectedRows.length === 0}
                    className="btn btn-sm btn-danger px-3"
                    style={{ fontSize: '12px' }}
                  >
                    {deleteLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Deleting...
                      </>
                    ) : (
                      `Delete Selected (${selectedRows.length})`
                    )}
                  </button>
                </div>

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CompanyManagement;